import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useCreateCompetition, type CompetitionFormat } from '../queries'
import styles from './CompetitionCreateForm.module.css'

// Slug is derived from the name so the admin never has to think about it, but stays
// editable — it's part of the competition's permanent identity and can't change later.
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function CompetitionCreateForm({ onCreated }: { onCreated: (id: string) => void }) {
  const currentYear = new Date().getFullYear()

  const [name, setName]                 = useState('')
  const [slug, setSlug]                 = useState('')
  const [slugTouched, setSlugTouched]   = useState(false)
  const [format, setFormat]             = useState<CompetitionFormat>('LEAGUE')
  const [externalLeagueId, setLeagueId] = useState('')
  const [season, setSeason]             = useState(currentYear)
  const [startsAt, setStartsAt]         = useState(`${currentYear}-01-01`)
  const [endsAt, setEndsAt]             = useState(`${currentYear}-12-31`)

  const create = useCreateCompetition()

  const effectiveSlug = slugTouched ? slug : slugify(name)
  const canSubmit = name.trim().length > 0 && effectiveSlug.length > 0 && !create.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    // exactOptionalPropertyTypes: omit the keys entirely rather than passing undefined.
    const input = {
      name:     name.trim(),
      slug:     effectiveSlug,
      format,
      startsAt: new Date(`${startsAt}T00:00:00Z`).toISOString(),
      endsAt:   new Date(`${endsAt}T23:59:59Z`).toISOString(),
      ...(externalLeagueId.trim()
        ? { externalLeagueId: externalLeagueId.trim(), season }
        : {}),
    }

    create.mutate(input, {
      onSuccess: (competition) => {
        setName('')
        setSlug('')
        setSlugTouched(false)
        setLeagueId('')
        onCreated(competition.id)
      },
    })
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Nova competição</h3>
      <p className={styles.hint}>
        O <code>código</code> e a temporada identificam a competição no provedor de dados
        (football-data.org). Ficam salvos aqui, então importar times, calendário e resultados
        depois não pede mais nada.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Nome</span>
            <input
              type="text"
              className={styles.input}
              value={name}
              placeholder="ex: Brasileirão 2026"
              onChange={(e) => setName(e.target.value)}
              disabled={create.isPending}
              required
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Slug</span>
            <input
              type="text"
              className={styles.input}
              value={effectiveSlug}
              placeholder="brasileirao-2026"
              onChange={(e) => { setSlugTouched(true); setSlug(e.target.value) }}
              disabled={create.isPending}
              required
            />
          </label>
        </div>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Formato</span>
            <select
              className={styles.input}
              value={format}
              onChange={(e) => setFormat(e.target.value as CompetitionFormat)}
              disabled={create.isPending}
            >
              <option value="LEAGUE">Liga (pontos corridos)</option>
              <option value="KNOCKOUT">Mata-mata / Copa</option>
            </select>
            <span className={styles.fieldHint}>
              {format === 'LEAGUE'
                ? 'Rodadas "Regular Season - N" viram a rodada N.'
                : 'Fases de grupo e mata-mata viram as rodadas 1–8.'}
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Código da competição</span>
            <input
              type="text"
              className={styles.input}
              value={externalLeagueId}
              placeholder="ex: BSA"
              onChange={(e) => setLeagueId(e.target.value)}
              disabled={create.isPending}
            />
            <span className={styles.fieldHint}>
              Ex.: BSA (Brasileirão Série A), BSB (Série B). Sem ele não dá para importar.
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Temporada</span>
            <input
              type="number"
              className={styles.input}
              value={season}
              min={1900}
              max={2100}
              onChange={(e) => setSeason(Number(e.target.value))}
              disabled={create.isPending}
            />
          </label>
        </div>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Início</span>
            <input
              type="date"
              className={styles.input}
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              disabled={create.isPending}
              required
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Término</span>
            <input
              type="date"
              className={styles.input}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              disabled={create.isPending}
              required
            />
          </label>
        </div>

        <button type="submit" className={styles.btn} disabled={!canSubmit}>
          {create.isPending
            ? <span className={styles.spinner} aria-hidden="true" />
            : <Plus size={14} />}
          {create.isPending ? 'Criando…' : 'Criar competição'}
        </button>
      </form>
    </div>
  )
}
